
export type Role = 'agent' | 'admin' | 'owner' | 'billing';

export interface Session {
  readonly user: {
    readonly id: string;
    readonly groupId: string;
    readonly email: string;
    readonly name: string;
    readonly roles: readonly Role[];
  };
  readonly group: { readonly id: string; readonly name: string };
  readonly roles: readonly Role[];
}

export interface PlatformSession {
  readonly platformAdmin: true;
}

export interface OnboardingSession {
  readonly onboarding: true;
  readonly email: string;
  /** Fresh single-use grant minted by `/auth/me` each poll; App.tsx stashes it so the wizard's Step 1
   *  set-password always has a grant (ticket 04). Optional: a body from before this change lacks it. */
  readonly passwordGrant?: string;
}

/** A bound, active user whose password is unset (clicked an invite link but never set one). Routed
 *  to set-password client-side; the grant is minted fresh by `/auth/me` on every poll (deadlock fix). */
export interface SetupIncompleteSession {
  readonly setupIncomplete: true;
  readonly email: string;
  readonly passwordGrant: string;
}

export function isPlatformSession(session: SessionResult): session is PlatformSession {
  return session !== UNAUTHENTICATED && 'platformAdmin' in session;
}

export function isOnboardingSession(session: SessionResult): session is OnboardingSession {
  return session !== UNAUTHENTICATED && 'onboarding' in session;
}

export function isSetupIncompleteSession(
  session: SessionResult,
): session is SetupIncompleteSession {
  return session !== UNAUTHENTICATED && 'setupIncomplete' in session;
}

export interface MeError {
  readonly error: string;
  readonly message: string;
}

export const UNAUTHENTICATED = null;
export type SessionOrNull = Session | typeof UNAUTHENTICATED;

export type SessionResult =
  | Session
  | PlatformSession
  | OnboardingSession
  | SetupIncompleteSession
  | typeof UNAUTHENTICATED;

export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

export async function fetchSession(signal?: AbortSignal): Promise<SessionResult> {
  const response = await fetch('/auth/me', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (response.status === 401) {
    return UNAUTHENTICATED;
  }
  if (!response.ok) {
    throw new Error(`Failed to load session (HTTP ${response.status})`);
  }
  return (await response.json()) as
    | Session
    | PlatformSession
    | OnboardingSession
    | SetupIncompleteSession;
}

// `billing` sits outside the ladder (rank -1): it implies nothing and only owner implies it,
// mirroring the server's scopesForRoles.
const ROLE_RANK: Record<Role, number> = { billing: -1, agent: 0, admin: 1, owner: 2 };

export function hasRole(session: SessionResult, role: Role): boolean {
  if (
    session === UNAUTHENTICATED ||
    isPlatformSession(session) ||
    isOnboardingSession(session) ||
    isSetupIncompleteSession(session)
  ) {
    return false;
  }
  if (role === 'billing') {
    return session.roles.some((r) => r === 'billing' || r === 'owner');
  }
  return session.roles.some((r) => ROLE_RANK[r] >= ROLE_RANK[role]);
}

export function hasAnyRole(session: SessionResult, ...roles: Role[]): boolean {
  return roles.some((r) => hasRole(session, r));
}
